import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';
import {
    Typography, Container, Box, Paper, TextField, Button, Alert,
    IconButton, Avatar, Fade, Snackbar, useTheme
} from '@mui/material';
import {
    ArrowBack,
    Save as SaveIcon,
    School as SchoolIcon
} from '@mui/icons-material';

const TurmaCriarPagina = () => {
    const navegar = useNavigate();
    const theme = useTheme();
    const [nome, setNome] = useState('');
    const [semestre, setSemestre] = useState('');
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setSucesso('');
        if (!nome) {
            setErro('O campo "Nome da Turma" é obrigatório.');
            return;
        }
        try {
            await api.post('/api/turmas/criar', { nome, semestre });
            setSucesso('Turma criada com sucesso!');
            setTimeout(() => navegar('/gerenciar-turmas'), 1500);
        } catch (err) {
            setErro(err.response?.data?.erro || 'Ocorreu um erro ao criar a turma.');
        }
    };

    return (
        <>
            <Snackbar
                open={!!sucesso}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: cores.sucesso.principal
                    }}
                >
                    {sucesso}
                </Alert>
            </Snackbar>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
                <Fade in={true} timeout={500}>
                    <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                        <Box component="form" onSubmit={lidarComSubmissao}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                                p: { xs: 2, sm: 3 },
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                color: 'white'
                            }}>
                                <IconButton
                                    onClick={() => navegar('/gerenciar-turmas')}
                                    sx={{
                                        mr: 2,
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)',
                                        }
                                    }}
                                >
                                    <ArrowBack />
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h4"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Criar Nova Turma
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}
                                    >
                                        Adicione uma nova turma ao sistema
                                    </Typography>
                                </Box>
                            </Box>

                            <Fade in={true} timeout={600}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        mb: 3,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 2,
                                        border: `1px solid ${cores.cinza.claro}`,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <Avatar
                                            sx={{
                                                mr: 2,
                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                color: cores.primaria.principal
                                            }}
                                        >
                                            <SchoolIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                                INFORMAÇÕES DA TURMA
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                                Preencha os detalhes da nova turma que será criada
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Fade>

                            <Box sx={{ p: { xs: 0, sm: 2 } }}>
                                <TextField
                                    label="Nome da Turma"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Digite um nome descritivo para identificar a turma (ex: Medicina 5º Período - Turma A)"
                                />

                                <TextField
                                    label="Semestre / Ano Letivo"
                                    variant="outlined"
                                    fullWidth
                                    value={semestre}
                                    onChange={(e) => setSemestre(e.target.value)}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                    helperText="Indique o período letivo desta turma (ex: 2023.2)"
                                />

                                {erro && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mt: 2,
                                            borderRadius: 2,
                                            backgroundColor: cores.erro.claro,
                                            borderColor: cores.erro.medio,
                                            color: cores.erro.escuro,
                                            '& .MuiAlert-icon': {
                                                alignItems: 'center'
                                            }
                                        }}
                                    >
                                        {erro}
                                    </Alert>
                                )}

                                <Box sx={{
                                    mt: 4,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 2,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navegar('/gerenciar-turmas')}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.5,
                                            px: 3,
                                            borderColor: cores.cinza.medio,
                                            color: cores.cinza.escuro,
                                            '&:hover': {
                                                borderColor: cores.cinza.escuro,
                                                bgcolor: 'rgba(71, 85, 105, 0.04)'
                                            }
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            py: 1.5,
                                            px: 3,
                                            bgcolor: cores.primaria.principal,
                                            fontWeight: 600,
                                            boxShadow: cores.sombras.suave,
                                            '&:hover': {
                                                bgcolor: cores.primaria.escuro,
                                            }
                                        }}
                                    >
                                        Salvar Turma
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>
            </Container>
        </>
    );
};

export default TurmaCriarPagina;